import { useRef, useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { sysColors } from "@/assets/theme/colors";
import { sumBy } from "lodash";
import { ChartData } from "./Chart";
import { useWindowSize } from "@/hooks/useWindowSize";
import ChartTooltip from "./ChartTooltip";

import classNames from "classnames";
import Tooltip from "@/components/Tooltip";
import Icon from "@/components/Icon";

import ChartLegend from "./ChartLegend";

const COLORS = [
  sysColors["sys-brand-surface-container-high"],
  sysColors["sys-brand-primary"],
  sysColors["sys-brand-error-container"],
  sysColors["sys-brand-on-error-container"]
];

export type DonutChartProps = {
  // Chart data
  data: ChartData;
  total: number;
  title: string;

  // Main header
  label: string;

  // Value display
  displayValue: string;

  // Additional text content
  description?: string;
  descriptionTwo?: string;

  // Tooltip configurations
  infoTooltip?: {
    title: string;
    supportingText: string;
  };
  successTooltip?: {
    supportingText: string;
  };

  // Layout
  className?: string;
};

function calculateSegments(data: ChartData, total: number) {
  let newData = data.map((item) => ({ ...item, label: item.value }));
  newData = newData.map((item) => {
    if (item.fixedSegment) {
      return { ...item, value: 0 };
    }
    if (total === 0 && data.length === 1 && item.value === 0) {
      return { ...item, value: 1 };
    }
    return item;
  });
  const remainingValue = total - sumBy(newData, "value");
  newData = [
    { name: "Remaining", value: total > 0 ? remainingValue : 0, disabled: true, label: remainingValue },
    ...newData
  ];
  const startAngle = total > 0 ? (remainingValue / total) * 360 - 270 : 90;
  return { newData, startAngle };
}

const RADIAN = Math.PI / 180;
const cy = 112;
const iR = 40;
const oR = 90;

const needle = (
  value: number,
  data: any[],
  cx: number,
  cy: number,
  iR: number,
  oR: number,
  color: string | undefined,
  title: string,
  name: string
) => {
  let total = 0;
  data.forEach((v) => {
    total += v.value;
  });

  const ang = 360.0 * (1 - value / total) + 90;
  const length = oR - iR;
  const sin = Math.sin(-RADIAN * ang);
  const cos = Math.cos(-RADIAN * ang);
  const r = 1;

  // Start from inner radius
  const x0 = cx + iR * cos;
  const y0 = cy + iR * sin;

  // Base points
  const xba = x0 + r * sin;
  const yba = y0 - r * cos;
  const xbb = x0 - r * sin;
  const ybb = y0 + r * cos;

  // Tip point
  const xp = x0 + length * cos;
  const yp = y0 + length * sin;

  // Additional point for rectangle
  const xc = xp + r * sin;
  const yc = yp - r * cos;
  const xd = xp - r * sin;
  const yd = yp + r * cos;

  return [
    <path
      key={`needle-${name}`}
      d={`M${xba} ${yba}L${xbb} ${ybb}L${xd} ${yd}L${xc} ${yc}L${xba} ${yba}`}
      stroke="#none"
      fill={color}
      onMouseEnter={(e) => {
        const tooltip = document.createElement("div");
        tooltip.className = "absolute p-1 bg-sys-brand-inverse-surface rounded z-50";
        tooltip.innerHTML = `
          <div class="p-2">
            <p class="text-label-large text-sys-brand-inverse-on-surface mb-2">${title}</p>
            <div class="flex gap-1 items-center">
              <div class="w-3 h-3 border border-sys-brand-inverse-on-surface bg-sys-brand-on-error-container" ></div>
              <p class="text-body-small text-sys-brand-inverse-on-surface">${name}: ${value} </p>
            </div>
          </div>
        `;
        tooltip.style.left = `${e.pageX + 10}px`;
        tooltip.style.top = `${e.pageY + 10}px`;
        document.body.appendChild(tooltip);
      }}
      onMouseLeave={() => {
        const tooltips = document.getElementsByClassName("absolute p-1 bg-sys-brand-inverse-surface rounded z-50");
        Array.from(tooltips).forEach((tooltip) => tooltip.remove());
      }}
      onMouseMove={(e) => {
        const tooltips = document.getElementsByClassName("absolute p-1 bg-sys-brand-inverse-surface rounded z-50");
        Array.from(tooltips).forEach((tooltip) => {
          (tooltip as HTMLElement).style.left = `${e.pageX + 10}px`;
          (tooltip as HTMLElement).style.top = `${e.pageY + 10}px`;
        });
      }}
    />
  ];
};

const DonutChart = (props: DonutChartProps) => {
  const [cx, setCx] = useState(0);
  const {
    data,
    total,
    title,
    label,
    displayValue,
    description,
    descriptionTwo,
    infoTooltip,
    successTooltip,
    className
  } = props;

  const { newData, startAngle } = calculateSegments(data, total);
  const fixedSegment = data.find((item) => item.fixedSegment);
  const value = fixedSegment?.value || 0;
  const { width } = useWindowSize();
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chartRef.current) {
      setCx(chartRef.current.getBoundingClientRect().width / 2);
    }
  }, [chartRef, width]);

  return (
    <div
      className={classNames(
        "flex flex-col items-start w-full",
        "border border-sys-brand-outline rounded-lg p-6",
        className
      )}
    >
      {/* Header with label and tooltips */}
      <div className="w-full h-full">
        <div className="flex items-center justify-between w-full">
          <p className="text-title-medium text-sys-brand-on-surface">{label}</p>
          <div className="flex items-center gap-2">
            {infoTooltip && (
              <Tooltip title={infoTooltip.title} supportingText={infoTooltip.supportingText} anchorClassName="p-1 -m-1">
                <Icon name="info" className="text-sys-brand-on-surface-variant" />
              </Tooltip>
            )}
            {successTooltip && (
              <Tooltip supportingText={successTooltip.supportingText} anchorClassName="p-1 -m-1">
                <Icon name="check_circle" className="text-extended-success-brand-color" />
              </Tooltip>
            )}
          </div>
        </div>
        {/* Value display */}
        <div className="text-display-small text-sys-brand-on-surface">{displayValue}</div>
        {/* Additional descriptive text */}
        {(description || descriptionTwo) && (
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            {description && (
              <>
                {description}
                {descriptionTwo && <br />}
              </>
            )}
            {descriptionTwo}
          </p>
        )}
      </div>

      {/* Chart */}
      <div ref={chartRef} className="w-full">
        <ResponsiveContainer width="100%" height={225}>
          <PieChart>
            <Pie
              data={newData}
              labelLine={false}
              outerRadius={90}
              innerRadius={40}
              fill={sysColors["sys-brand-scrim"]}
              dataKey="value"
              startAngle={startAngle}
              endAngle={startAngle - 360}
            >
              {newData.map((entry, index) => (
                <Cell
                  key={`cell-${entry.name}`}
                  fill={COLORS[index % COLORS.length]}
                  stroke={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>

            <RechartsTooltip
              content={({ active = false, payload }) => {
                const items =
                  payload?.map((entry: any) => ({
                    name: entry.payload.name,
                    value: entry.payload.label || entry.value,
                    color: entry.payload.fill || entry.color
                  })) || [];

                return <ChartTooltip active={active} title={title} items={items} />;
              }}
            />

            {value > 0 &&
              needle(
                value,
                newData,
                cx,
                cy,
                iR,
                oR,
                sysColors["sys-brand-on-error-container"],
                title,
                fixedSegment?.name || ""
              )}
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <ChartLegend
        items={newData
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => !item.disabled)
          .map(({ item, index }) => ({
            name: item.name,
            color: COLORS[index % COLORS.length],
            visible: true
          }))}
      />
    </div>
  );
};

export default DonutChart;
